import React, { useRef } from 'react';

import styles from './index.module.scss';
import { useKeyListener, useOutsideClick } from '@navi/web-ui/lib/hooks';
import { IconButton, Typography } from '@navi/web-ui/lib/primitives';
import { CloseIcon } from '@navi/web-ui/lib/icons';

import cx from 'classnames';
import { ESCAPE_KEY } from '@navi/web-ui/lib/constants/Components';
import { IconButtonSizes, IconButtonVariants } from '@navi/web-ui/lib/primitives/IconButton/types';

interface IOverlay extends React.PropsWithChildren {
  visible: boolean;
  containerClasses?: string;
  header?: string | React.ReactNode;
  onClose: () => void;
  closeOnOutsideClick?: boolean;
  headerColor?: string;
  headerClassName?: string;
  contentClassName?: string;
  iconButtonSize?: IconButtonSizes;
  iconButtonVariant?: IconButtonVariants;
  isHeaderRequired?: boolean;
  showCloseIcon?: boolean;
  contentWrapperClass?: string;
  overlappingIcon?: React.ReactNode;
}

const Modal: React.FC<IOverlay> = props => {
  const {
    children,
    visible,
    containerClasses,
    header,
    onClose,
    headerColor,
    headerClassName,
    contentClassName,
    iconButtonSize = 'medium',
    iconButtonVariant = 'borderless-secondary',
    closeOnOutsideClick,
    isHeaderRequired = true,
    showCloseIcon = true,
    contentWrapperClass,
    overlappingIcon
  } = props;
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close modal on ESC key press
  useKeyListener(ESCAPE_KEY, onClose, visible);

  useOutsideClick(overlayRef, () => {
    if (closeOnOutsideClick) {
      onClose();
    }
  });

  return (
    <>
      {visible && (
        <div className={cx(styles.overlay, containerClasses)}>
          <div ref={overlayRef} className={cx(styles.contentWrapper, contentWrapperClass)}>
            {overlappingIcon && <div className={styles.overlappingIcon}>{overlappingIcon}</div>}
            {isHeaderRequired ? (
              <div className={cx(styles.header, headerClassName)}>
                {typeof header === 'string' ? (
                  <Typography variant={'h3'} color={headerColor}>
                    {header || ''}
                  </Typography>
                ) : (
                  header
                )}
                {showCloseIcon && (
                  <IconButton
                    Icon={CloseIcon}
                    className={styles.closeIcon}
                    size={iconButtonSize}
                    onClick={onClose}
                    variant={iconButtonVariant}
                    data-testid="close-icon"
                  />
                )}
              </div>
            ) : null}
            <div className={cx(styles.content, contentClassName)}>{children}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default Modal;
